import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ToastComponent } from './modules/shared/components/toast/toast.component';
import { UploadProgressComponent } from './modules/shared/components/upload-progress/upload-progress.component';
import { version as localVersion } from '../environments/version';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastService } from './core/services/toast.service';
import { AuthService } from './core/auth/services/auth.service';
import { ContemplationFloatingDictationComponent } from './modules/contemplations/components/contemplation-floating-dictation/contemplation-floating-dictation.component';
import { CoachingMessengerComponent } from './modules/coaching/components/coaching-messenger/coaching-messenger.component';
import { CoachingUnreadDockComponent } from './modules/coaching/components/coaching-unread-dock/coaching-unread-dock.component';
import { SetupTasksBoxComponent } from './modules/shared/components/setup-tasks-box/setup-tasks-box.component';
import { PhoneSetupService } from './core/services/phone-setup.service';
import { CoachingRealtimeService } from './modules/coaching/services/coaching-realtime.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ToastComponent,
    UploadProgressComponent,
    ContemplationFloatingDictationComponent,
    CoachingMessengerComponent,
    CoachingUnreadDockComponent,
    SetupTasksBoxComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  /** Unread dock: ADMIN / TRAINER only — never mount for end users. */
  showCoachUnreadDock = false;
  /** Messenger FAB: end users only (staff use inbox / unread dock). */
  showUserMessenger = false;
  /** Journal FAB: logged-in users only. */
  showJournalFab = false;
  /** Finish-setup checklist: end users only. */
  showSetupBox = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private auth: AuthService,
    // Eagerly construct so socket connects after login (providedIn root + ctor subscription).
    private coachingRealtime: CoachingRealtimeService,
    private router: Router,
    private phoneSetup: PhoneSetupService
  ) {
    this.checkVersion();

    setInterval(() => {
      this.checkVersion();
    }, 1000 * 60 * 10); // 10 minutes interval
  }

  ngOnInit(): void {
    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const loggedIn = !!this.auth.getToken() && !!this.auth.currentUser;
      const staff = this.coachingRealtime.isStaffCoach();
      this.showCoachUnreadDock = loggedIn && staff;
      this.showUserMessenger = loggedIn && !staff;
      this.showJournalFab = loggedIn;
      this.showSetupBox = loggedIn && !staff;
      if (!loggedIn) {
        this.phoneSetup.resetPrompt();
      } else {
        this.maybePromptPhoneSetup();
      }
    });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.maybePromptPhoneSetup());
  }

  /** Existing end users without a verified WhatsApp number get the setup dialog once per login. */
  private maybePromptPhoneSetup(): void {
    if (!this.auth.getToken() || !this.auth.currentUser) return;
    const roles = this.auth.getRoles().map((r) => String(r).toUpperCase());
    if (roles.some((r) => r.includes('ADMIN') || r.includes('TRAINER') || r.includes('SUPPORT'))) return;
    const url = this.router.url.split('?')[0].toLowerCase();
    if (url === '/' || url.startsWith('/auth') || url.startsWith('/onboarding') || url.includes('checkout')) return;
    void this.phoneSetup.promptIfNeeded();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

checkVersion() {
  // Add cache-busting headers to ensure fresh fetch from server
  const headers = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  });

  // Add timestamp parameter to prevent caching
  const timestamp = new Date().getTime();
  const url = `/assets/version.json?t=${timestamp}`;

  this.http.get<{ version: string }>(url, { headers })
    .subscribe({
      next: (remote) => {
        if (remote.version !== localVersion) {
          this.toastService.showInfo('We’ve made some improvements. Please reload the page to get the new version.');
          setTimeout(() => {
            location.reload();
          }, 3000);
        }
      },
      error: (error) => {
        console.warn('Failed to check version:', error);
        // Don't reload on error, just log the issue
      }
    });
  }
}
